import { getNavLabelForAppPath } from '@mcn-platform/models';

/**
 * Maps admin editor URL prefixes to their list view for breadcrumbs.
 * Parent **labels** come from NavStore (`getNavLabelForAppPath`) so they match the sidebar;
 * {@link LIST_PATH_LABEL_FALLBACK} covers list routes that are not in the nav tree.
 * Longer prefixes are matched first (see sort in getEditorListCrumb).
 */
const ENTRIES: { prefix: string; listPath: string }[] = [
  { prefix: '/app/email-template-editor', listPath: '/app/email-templates' },
  { prefix: '/app/engagement-flow-editor', listPath: '/app/engagement-flows' },
  { prefix: '/app/libraryShare-editor', listPath: '/app/libraryShares' },
  { prefix: '/app/shopify-shop-editor', listPath: '/app/shopify-shops' },
  { prefix: '/app/repository-editor', listPath: '/app/repositories' },
  { prefix: '/app/questionnaire-editor', listPath: '/app/portal-questionnaires' },
  { prefix: '/app/product-editor', listPath: '/app/products' },
  { prefix: '/app/category-editor', listPath: '/app/ecommerce-categories' },
  { prefix: '/app/web-page-editor', listPath: '/app/web-pages' },
  { prefix: '/app/course-editor', listPath: '/app/courses' },
  { prefix: '/app/microcast-editor', listPath: '/app/microcasts' },
  { prefix: '/app/document-editor', listPath: '/app/documents' },
  { prefix: '/app/program-editor', listPath: '/app/programs' },
  { prefix: '/app/guide-editor', listPath: '/app/guides' },
  { prefix: '/app/membership-editor', listPath: '/app/portal-memberships' },
  { prefix: '/app/member-editor', listPath: '/app/portal-members' },
  { prefix: '/app/kicker-editor', listPath: '/app/portal-kickers' },
  { prefix: '/app/question-editor', listPath: '/app/portal-questions' },
  { prefix: '/app/lead-editor', listPath: '/app/leads' },
  { prefix: '/app/system-prompts', listPath: '/app/system-prompts' },
  { prefix: '/app/client-ai-config', listPath: '/app/client-ai-configs' },
  { prefix: '/app/certification/', listPath: '/app/certifications' },
  { prefix: '/app/instructor/', listPath: '/app/instructors' },
  { prefix: '/app/meeting/', listPath: '/app/meetings' },
  { prefix: '/app/hub/', listPath: '/app/hubs' },
  { prefix: '/app/library/', listPath: '/app/libraries' },
  { prefix: '/app/customer/', listPath: '/app/customers' },
  { prefix: '/app/discussion/', listPath: '/app/discussion' },
  { prefix: '/app/profile/', listPath: '/app/profiles' },
  { prefix: '/app/userOrg/', listPath: '/app/users' },
  { prefix: '/app/organization/', listPath: '/app/organizations' },
  { prefix: '/app/web-crawl', listPath: '/app/web-crawl' },
  { prefix: '/app/aiconfig', listPath: '/app/portals' },
  { prefix: '/app/details', listPath: '/app/portals' },
];

const SORTED = [...ENTRIES].sort((a, b) => b.prefix.length - a.prefix.length);

/**
 * List destinations that exist in routes but not in {@link NavStore} `items` — keep labels here.
 */
const LIST_PATH_LABEL_FALLBACK: Record<string, string> = {
  '/app/portal-questionnaires': 'Questionnaires',
  '/app/portal-questions': 'Questions',
  '/app/leads': 'Leads',
};

function listLabelForPath(listPath: string): string {
  return getNavLabelForAppPath(listPath)
    ?? LIST_PATH_LABEL_FALLBACK[listPath]
    ?? listPath;
}

function assetListPath(pathname: string): string | null {
  if (!pathname.startsWith('/app/asset')) {
    return null;
  }
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2 || segments[0] !== 'app' || segments[1] !== 'asset') {
    return null;
  }
  const type = segments[2];
  if (type && ['video', 'image', 'pdf', 'other'].includes(type)) {
    return `/app/assets/${type}`;
  }
  return '/app/assets/other';
}

/**
 * Returns the parent list link/label for the current editor path, or null if unknown.
 */
export function getEditorListCrumb(pathname: string): { listPath: string; listLabel: string } | null {
  const assetPath = assetListPath(pathname);
  if (assetPath) {
    return { listPath: assetPath, listLabel: listLabelForPath(assetPath) };
  }
  for (const row of SORTED) {
    if (pathname.startsWith(row.prefix)) {
      return { listPath: row.listPath, listLabel: listLabelForPath(row.listPath) };
    }
  }
  return null;
}
